import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("kh_admin_token");
  if (token && config.url && config.url.includes("/admin/")) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- Curated SEO content (primary) ----
export const getCategories = (lang) => api.get("/content/categories", { params: { lang } }).then((r) => r.data);
export const getCategory = (slug, lang) => api.get(`/content/category/${slug}`, { params: { lang } }).then((r) => r.data);
export const getContentBrand = (slug, params) => api.get(`/content/brand/${slug}`, { params }).then((r) => r.data);
export const getFeatured = () => api.get("/content/featured").then((r) => r.data);
export const getLandingPage = (slug, lang) => api.get(`/content/landing/${slug}`, { params: { lang } }).then((r) => r.data);

// ---- i18n ----
export const getLanguages = () => api.get("/i18n/languages").then((r) => r.data);
export const getUiStrings = (lang) => api.get(`/i18n/ui/${lang}`).then((r) => r.data);
export const adminAddLanguage = (payload) => api.post("/admin/i18n/languages", payload).then((r) => r.data);
export const adminGetLlmSettings = () => api.get("/admin/settings/llm").then((r) => r.data);
export const adminSaveLlmSettings = (payload) => api.post("/admin/settings/llm", payload).then((r) => r.data);

// ---- CMS images (homepage hero / category cards / brand tiles) ----
export const getCmsOverrides = () => api.get("/cms/overrides").then((r) => r.data);
export const adminGetCmsSlots = () => api.get("/admin/cms/slots").then((r) => r.data);
export const adminUploadCmsImage = ({ slot, file }) => {
  const form = new FormData();
  form.append("slot", slot);
  form.append("file", file);
  return api.post("/admin/cms/image", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  }).then((r) => r.data);
};
export const adminDeleteCmsImage = (slot) =>
  api.delete("/admin/cms/image", { params: { slot } }).then((r) => r.data);

// ---- Backorder lead capture ----
export const requestBackorder = (payload) => api.post("/backorder/request", payload).then((r) => r.data);
export const adminGetBackorders = (params) => api.get("/admin/backorder/requests", { params }).then((r) => r.data);
export const adminUpdateBackorder = (id, payload) => api.patch(`/admin/backorder/requests/${id}`, payload).then((r) => r.data);
export const adminGetSmtpSettings = () => api.get("/admin/settings/smtp").then((r) => r.data);
export const adminSaveSmtpSettings = (payload) => api.post("/admin/settings/smtp", payload).then((r) => r.data);
export const adminTestSmtp = () => api.post("/admin/settings/smtp/test").then((r) => r.data);

// ---- Live catalog (on-site cart / PDP) ----
export const getProduct = (handle, lang) => api.get(`/catalog/product/${handle}`, { params: { lang } }).then((r) => r.data);
export const getCatalogProducts = (params) => api.get("/catalog/products", { params }).then((r) => r.data);
export const postCheckout = (payload) => api.post("/cart/checkout", payload).then((r) => r.data);

// Same-origin media proxy (bypasses Shopify CDN hotlink protection).
// `width` downscales the image at the Shopify CDN source — always pass a sensible
// width so we never stream multi-MB originals (keeps product pages fast).
const _ALLOWED_W = [200, 400, 600, 800, 1000, 1200, 1600, 2000];
const _snapW = (w) => _ALLOWED_W.find((x) => x >= w) || 1200;

export const proxyImage = (url, width) => {
  if (!url) return null;
  if (url.startsWith("/api/")) {
    // already proxied — add a width if one isn't present
    if (width && !/[?&]w=/.test(url)) return `${url}${url.includes("?") ? "&" : "?"}w=${_snapW(width)}`;
    return url;
  }
  if (url.startsWith("http")) {
    const w = width ? `&w=${_snapW(width)}` : "";
    return `/api/media?u=${encodeURIComponent(url)}${w}`;
  }
  return url;
};

// ---- Shopify status (optional live mode) ----
export const getShopStatus = () => api.get("/shopify/status").then((r) => r.data);

// ---- Admin ----
export const adminLogin = (payload) => api.post("/admin/login", payload).then((r) => r.data);
export const adminMe = () => api.get("/admin/me").then((r) => r.data);
export const getDashboard = (params) => api.get("/admin/analytics/dashboard", { params }).then((r) => r.data);
export const getBots = (params) => api.get("/admin/analytics/bots", { params }).then((r) => r.data);
export const getVisitors = (params) => api.get("/admin/analytics/visitors", { params }).then((r) => r.data);
export const getInstallUrl = () => api.get("/shopify/oauth/install-url").then((r) => r.data);

// ---- Visitor Audit (session-level + conversions) ----
export const getAuditSummary = (params) => api.get("/admin/analytics/audit-summary", { params }).then((r) => r.data);
export const getSessions = (params) => api.get("/admin/analytics/sessions", { params }).then((r) => r.data);
export const getSessionDetail = (id) => api.get(`/admin/analytics/sessions/${id}`).then((r) => r.data);
export const clearVisitorLogs = (older_than_days = 0) => api.post("/admin/analytics/clear-logs", null, { params: { older_than_days } }).then((r) => r.data);
export const getConversionSettings = () => api.get("/admin/shopify/conversion-settings").then((r) => r.data);
export const saveConversionSettings = (payload) => api.post("/admin/shopify/conversion-settings", payload).then((r) => r.data);
export const registerWebhook = () => api.post("/admin/shopify/webhooks/register").then((r) => r.data);
export const getShopifyOrders = (params) => api.get("/admin/shopify/orders", { params }).then((r) => r.data);

// Visitor session id (shared with usePageTracker's kh_sid key).
export const getVisitorSessionId = () => {
  try { return localStorage.getItem("kh_sid") || undefined; } catch { return undefined; }
};

export const formatPrice = (money) => {
  if (!money) return "";
  try {
    return new Intl.NumberFormat("en-IE", { style: "currency", currency: money.currency || "EUR" }).format(money.amount);
  } catch {
    return `\u20ac${Number(money.amount).toFixed(2)}`;
  }
};

export const CATEGORY_NAV = [
  { slug: "horse-rugs", name: "Horse Rugs" },
  { slug: "saddle-pads", name: "Saddle Pads" },
  { slug: "fly-veils", name: "Fly Veils" },
  { slug: "boots-bandages", name: "Boots & Bandages" },
  { slug: "bridles-tack", name: "Bridles & Tack" },
  { slug: "grooming", name: "Grooming Deluxe" },
  { slug: "dog", name: "Dog" },
  { slug: "stable-accessories", name: "Stable & Accessories" },
  { slug: "rider", name: "Rider" },
];
export const BRAND_NAV = [
  { slug: "kentucky", name: "Kentucky Horsewear", logo: "/brands/kentucky.png" },
  { slug: "dyon", name: "Dy'on", logo: "/brands/dyon.png" },
  { slug: "velari", name: "Velari", logo: "/brands/velari.png" },
  { slug: "grooming-deluxe", name: "Grooming Deluxe", logo: "/brands/grooming.png" },
];
